import React, { createContext, useContext, useState, ReactNode } from "react";
import WarningModal from "../components/WarningModal";

interface WarningModalOptions {
  title?: string;
  message: string;
  dismissLabel?: string;
  yesLabel?: string;
  hideYesButton?: boolean;
  onConfirm?: () => void;
  onDismiss?: () => void;
}

interface WarningModalContextType {
  showWarning: (options: WarningModalOptions) => void;
  hideWarning: () => void;
}

const WarningModalContext = createContext<WarningModalContextType | undefined>(undefined);

export const useWarningModal = () => {
  const context = useContext(WarningModalContext);
  if (!context) {
    throw new Error("useWarningModal must be used within a WarningModalProvider");
  }
  return context;
};

interface WarningModalProviderProps {
  children: ReactNode;
}

// This can and should be extended in the future to support multiple modal types

export const WarningModalProvider: React.FC<WarningModalProviderProps> = ({ children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [options, setOptions] = useState<WarningModalOptions | null>(null);

  const showWarning = (warningOptions: WarningModalOptions) => {
    setOptions(warningOptions);
    setIsVisible(true);
  };

  const hideWarning = () => {
    setIsVisible(false);
    setOptions(null);
  };

  const handleConfirm = () => {
    options?.onConfirm?.();
    hideWarning();
  };

  return (
    <WarningModalContext.Provider value={{ showWarning, hideWarning }}>
      {children}
      {isVisible && options && (
        <WarningModal
          item={true}
          msg={options.message}
          action={handleConfirm}
          dismissLabel={options.dismissLabel || "Cancel"}
          yesLabel={options.yesLabel || "OK"}
          hideYesButton={options.hideYesButton || false}
          dismissAction={true}
        />
      )}
    </WarningModalContext.Provider>
  );
};
